/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownIcon, ArrowUpIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { getHAEntities, saveHubDevices, type HubDevice } from "@/api/homeassistant";

interface Props {
  current: HubDevice[];
  onClose: () => void;
}

const GROUPS: Record<string, string> = {
  light: "Lights",
  switch: "Switches",
  fan: "Fans",
  cover: "Covers & blinds",
  vacuum: "Vacuums",
  camera: "Cameras",
  scene: "Scenes",
  script: "Scripts",
  button: "Buttons",
  input_button: "Buttons",
  input_boolean: "Toggles",
  automation: "Automations",
  climate: "Climate",
  sensor: "Sensors",
  binary_sensor: "Binary sensors",
  lock: "Locks (read-only)",
  person: "People",
  weather: "Weather",
};

/** Full-screen chooser: tick the entities that should become buttons on the display. */
export const DevicePicker: React.FC<Props> = ({ current, onClose }) => {
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<{ entityId: string; name?: string }[]>(() =>
    current.map((d) => ({ entityId: d.entityId })),
  );
  const [query, setQuery] = useState("");

  const { data: entities = [], isLoading, error } = useQuery({ queryKey: ["ha-entities"], queryFn: getHAEntities, retry: false });
  const byId = useMemo(() => new Map(entities.map((e) => [e.entityId, e])), [entities]);
  const isPicked = (id: string) => picked.some((p) => p.entityId === id);

  const save = useMutation({
    mutationFn: () => saveHubDevices(picked),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["hub-ha"] });
      onClose();
    },
  });

  const q = query.trim().toLowerCase();
  const groups = useMemo(() => {
    const m = new Map<string, typeof entities>();
    for (const e of entities) {
      if (q && !`${e.name} ${e.entityId}`.toLowerCase().includes(q)) continue;
      const g = GROUPS[e.domain] ?? e.domain;
      m.set(g, [...(m.get(g) ?? []), e]);
    }
    return [...m.entries()];
  }, [entities, q]);

  const toggle = (id: string) => setPicked((cur) => (cur.some((p) => p.entityId === id) ? cur.filter((p) => p.entityId !== id) : [...cur, { entityId: id }]));
  const move = (i: number, d: -1 | 1) =>
    setPicked((cur) => {
      const j = i + d;
      if (j < 0 || j >= cur.length) return cur;
      const n = [...cur];
      [n[i], n[j]] = [n[j], n[i]];
      return n;
    });
  const rename = (i: number, name: string) => setPicked((cur) => cur.map((p, k) => (k === i ? { ...p, name } : p)));

  return (
    <div className="hub-picker" role="dialog" aria-label="Choose devices" onPointerDown={(e) => e.stopPropagation()}>
      <header>
        <h2>Display devices</h2>
        <button type="button" onClick={onClose} aria-label="Cancel">
          <XMarkIcon />
        </button>
      </header>

      <div className="hub-picker-body">
        <section>
          <h3>
            On the display <em>{picked.length}</em>
          </h3>
          {picked.length === 0 && <p className="hub-mute">Nothing yet — tick devices on the right.</p>}
          <ol>
            {picked.map((p, i) => (
              <li key={p.entityId}>
                <input
                  value={p.name ?? ""}
                  onChange={(e) => rename(i, e.target.value)}
                  placeholder={byId.get(p.entityId)?.name ?? p.entityId}
                  aria-label={`Name of ${p.entityId}`}
                />
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">
                  <ArrowUpIcon />
                </button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === picked.length - 1} aria-label="Move down">
                  <ArrowDownIcon />
                </button>
                <button type="button" onClick={() => toggle(p.entityId)} aria-label="Remove">
                  <XMarkIcon />
                </button>
              </li>
            ))}
          </ol>
        </section>

        <section>
          <input className="hub-picker-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search Home Assistant…" aria-label="Search entities" />
          {isLoading && <p className="hub-mute">Loading…</p>}
          {error && <p className="hub-err">{(error as Error).message}</p>}
          <div className="hub-picker-list">
            {groups.map(([group, items]) => (
              <div key={group}>
                <h4>{group}</h4>
                {items.map((e) => (
                  <label key={e.entityId}>
                    <input type="checkbox" checked={isPicked(e.entityId)} onChange={() => toggle(e.entityId)} />
                    <span>{e.name}</span>
                    <small>{e.entityId}</small>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </section>
      </div>

      <footer>
        {save.isError && <span className="hub-err">{(save.error as Error).message}</span>}
        <button type="button" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="hub-primary" onClick={() => save.mutate()} disabled={save.isPending}>
          Save
        </button>
      </footer>
    </div>
  );
};
